import { createSelector } from '@reduxjs/toolkit'
import type { AgenticHubState, SearchFormValues } from './agenticHubSlice'
import { tabPendingCount } from '../utils/tab-counts'

type SliceRoot = { agenticHub: AgenticHubState }

const selectTabs = (s: SliceRoot) => s.agenticHub.tabs
const selectUnreadByEmail = (s: SliceRoot) => s.agenticHub.unreadByEmail
const selectHubPageMounted = (s: SliceRoot) => s.agenticHub.hubPageMounted
// The selected mailbox = the active tab's mailbox (selecting a mailbox switches the active tab to one
// of its tabs). Lets the browser title scope to that mailbox without threading the localStorage
// `selectedEmail` into the store.
const selectActiveMailbox = (s: SliceRoot): string | null => {
    const id = s.agenticHub.activeTabId
    return id ? (s.agenticHub.tabs[id]?.tab.sender_email ?? null) : null
}

export const selectSenderEmailByTab = createSelector(selectTabs, (tabs) => {
    const out: Record<string, string | null | undefined> = {}
    for (const id of Object.keys(tabs)) out[id] = tabs[id]?.tab.sender_email
    return out
})

export const selectPendingCountByEmail = createSelector(selectTabs, (tabs) => {
    const counts: Record<string, number> = {}
    for (const tabState of Object.values(tabs)) {
        const email = tabState.tab.sender_email
        const count = tabPendingCount(tabState)
        if (email && count > 0) {
            counts[email] = (counts[email] ?? 0) + count
        }
    }
    return counts
})

// Pending / unread for the SELECTED mailbox (the active tab's mailbox), mirroring the EmailSelector
// badge — the browser tab title (hooks/useTabTitle) shows these so the number matches the mailbox the
// dispatcher is viewing, not a cross-mailbox total. Gated on `hubPageMounted` so the title reverts to
// its base off the AI Hub page. Each resolves to a primitive, so useSelector re-renders only when the
// number actually changes.
export const selectActiveMailboxQuestionCount = createSelector(
    [selectPendingCountByEmail, selectActiveMailbox, selectHubPageMounted],
    (byEmail, email, mounted) => (mounted && email ? (byEmail[email] ?? 0) : 0)
)

export const selectActiveMailboxUnreadCount = createSelector([selectUnreadByEmail, selectActiveMailbox, selectHubPageMounted], (byEmail, email, mounted) =>
    mounted && email ? (byEmail[email] ?? 0) : 0
)

export const selectSearchFormByTab = createSelector(selectTabs, (tabs) => {
    const map: Record<string, SearchFormValues | null> = {}
    for (const tabId of Object.keys(tabs)) {
        map[tabId] = tabs[tabId]?.searchForm ?? null
    }
    return map
})

/**
 * Mirrors the server's `hasActiveRuns(tabId)` guard at apps/services/general-agent-service/
 * src/controllers/tabs.ts:70 — any registered run on either lane blocks mode/model writes
 * with a 409 AGENT_BUSY. The chat-lane-only `isStreaming || isSending` gate the UI used to
 * read missed search-pipeline scoring runs (incident DAT|ZS1TtgZZ, 2026-05-27). Read this
 * in any UI that gates a mode/model change, so the dropdown is locked whenever the server
 * would reject the write.
 */
export const selectTabIsBusy = (state: SliceRoot, tabId: string | null | undefined): boolean => {
    if (!tabId) return false
    const tab = state.agenticHub.tabs[tabId]
    if (!tab) return false
    return !!(tab.chatRuntime.isStreaming || tab.chatRuntime.isSending || tab.searchRuntime.isStreaming || tab.searchRuntime.isSending)
}
